import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { MDBDataTable } from "mdbreact";
import { useEffect, useState } from "react";
import api from "../../services/api";
import "./DashboardStatistics.css";
import "./datatables.scss";

/* ================================
   Utils
================================ */
const formatCurrency = (amount) => {
    if (typeof amount !== "number") amount = 0;
    return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};


const formatCurrencyPdf = (amount) => {
    if (typeof amount !== 'number') amount = Number(amount) || 0;
    return "Rs. " + new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    let d;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        // Date-only string — parse as local time, not UTC
        const [y, m, day] = dateString.split("-");
        d = new Date(`${y}-${m}-${day}T00:00:00`);
    } else {
        d = new Date(dateString);
    }

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
};
const formatDateWithoutTime = (dateString) => {
    if (!dateString) return "N/A";

    const d = new Date(dateString);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
};

/* ================================
   Icons (inline SVG)
================================ */
const ReceiptIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z" />
        <path d="M14 2v6h6" />
        <path d="M9 14h6" />
        <path d="M9 17h6" />
    </svg>
);

const ZapIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);

const CalendarIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const RefundIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <path d="M12 2v8l-4-4" />
        <path d="M8 6L4 10" />
        <path d="M21.17 13.17a2.5 2.5 0 0 0 0-3.54l-2.63-2.63a2.5 2.5 0 0 0-3.54 0L3 21.25V21c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v0a2.5 2.5 0 0 0 0-3.54Z" />
    </svg>
);

const PaymentIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <rect width="20" height="14" x="2" y="5" rx="2" />
        <line x1="2" x2="22" y1="10" y2="10" />
    </svg>
);

const DiscountIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" {...props}>
        <path d="M21.3 15.3a2.4 2.4 0 0 1 0 3.4l-2.6 2.6a2.4 2.4 0 0 1-3.4 0L3 8.4V3h5.4l12.9 12.9Z" />
        <line x1="6" x2="6" y1="9" y2="9" />
    </svg>
);

/* ================================
   Shared Components
================================ */
function DetailBox({ title, children }) {
    return (
        <div className="card shadow-sm rounded-3 border-0">
            <div className="card-body">
                <h2 className="h4 fw-bold text-dark mb-4">{title}</h2>
                {children}
            </div>
        </div>
    );
}

function StatCard({ title, amount, count, Icon, iconBg, iconColor, isSelected, onClick }) {
    return (
        <div
            onClick={onClick}
            className={`summary-card ${isSelected ? "active" : ""}`}
        >
            <div className="summary-card-header">
                <div>
                    <h6 className="fw-bold text-dark mb-1">{title}</h6>
                    <p className="text-muted small mb-0">Tap to view detailed records</p>
                </div>
                <div className={`summary-icon ${iconBg}`}>
                    {Icon ? <Icon className={`icon-md ${iconColor}`} /> : null}
                </div>
            </div>

            <div className="summary-body mt-3">
                <h4 className="fw-bold text-dark mb-1">{formatCurrency(amount)}</h4>
                <p className="small text-muted mb-0">
                    {count} {count === 1 ? "record" : "records"}
                </p>
            </div>
        </div>
    );
}

function DateStatCard({
    title,
    amount,
    count,
    selectedDate,
    onDateChange,
    isLoading,
    error,
    isSelected,
    onClick,
    data,
}) {
    return (
        <div
            onClick={onClick}
            className={`summary-card ${isSelected ? "active" : ""} ${!isLoading && !error && !!data ? "cursor-pointer" : ""
                }`}
        >
            <div className="summary-card-header mb-2">
                <div>
                    <h6 className="fw-bold text-dark mb-1">{title || "Stats by Date"}</h6>
                    <input
                        type="date"
                        className="form-control form-control-sm modern-date-input mt-2"
                        value={selectedDate}
                        onChange={onDateChange}
                        disabled={isLoading}
                    />
                </div>
                <div className="summary-icon bg-gradient-info">
                    <CalendarIcon className="icon-md text-white" />
                </div>
            </div>

            <div className="summary-body mt-2">
                {isLoading ? (
                    <div className="d-flex align-items-center">
                        <div
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                        >
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <span className="ms-2 text-muted small">Loading...</span>
                    </div>
                ) : error ? (
                    <p className="small text-danger mt-1 mb-0 fw-medium">{error}</p>
                ) : data ? (
                    <div>
                        <h4 className="fw-bold text-dark mb-1">{formatCurrency(amount)}</h4>
                        <p className="small text-muted mb-0">
                            {count} {count === 1 ? "record" : "records"}
                        </p>
                    </div>
                ) : (
                    <p className="small text-muted mt-1 mb-0">
                        Select a date to see stats.
                    </p>
                )}
            </div>
        </div>
    );
}


function RefundsTable({ refundData }) {
    if (!refundData || refundData.length === 0)
        return <p className="text-muted">No refund data available.</p>;

    const formatCurrency = (amount) =>
        amount?.toLocaleString("en-IN", { style: "currency", currency: "INR" });

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";

        const d = new Date(dateString);

        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();

        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;

        return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
    };


    const data = {
        columns: [
            { label: "S.No", field: "sno", sort: "asc", width: 60 },
            { label: "Roll No.", field: "roll_no", sort: "asc", width: 100 },
            { label: "Name", field: "name", sort: "asc", width: 150 },
            { label: "Course", field: "course_name", sort: "asc", width: 150 },
            { label: "Technology", field: "technology_name", sort: "asc", width: 150 },
            { label: "Amount", field: "amount", sort: "asc", width: 100 },
            { label: "Reason", field: "reason", sort: "asc", width: 150 },
            { label: "Payment ID", field: "payment_id", sort: "asc", width: 150 },
            { label: "Refund Date", field: "refund_date", sort: "asc", width: 150 },
            { label: "College", field: "college", sort: "asc", width: 100 },
            { label: "Branch", field: "branch", sort: "asc", width: 100 },
            { label: "Cashier Name", field: "cashier_name", sort: "asc", width: 120 },
            { label: "Cash Counter", field: "cash_counter", sort: "asc", width: 120 },
        ],

        rows: refundData.map((item, index) => ({
            sno: index + 1,
            roll_no: item.roll_no || "N/A",
            name: item.name || item.first_name || "N/A",
            course_name: item.course_name || "",
            technology_name: item.technology_name || "",
            amount: (
                <span className="text-danger fw-semibold">
                    {formatCurrency(item.refund_amount || item.amount || 0)}
                </span>
            ),
            reason: item.refund_reason || "N/A",
            payment_id: (
                <span className="text-muted small">{item.payment_id || "N/A"}</span>
            ),
            refund_date: (
                <span className="small">
                    {formatDate(item.refund_given_date || item.refund_date)}
                </span>
            ),
            college: item.college || "N/A",
            branch: Array.isArray(item.branch)
                ? item.branch.join(", ")
                : item.branch || "N/A",
            cashier_name: item.cashier_name || item.refund_given_by || "N/A",
            cash_counter: item.refund_cashcounter || item.cash_counter_name || "N/A",
        })),
    };

    return (
        <div className="datatable-container fade-in">
            <MDBDataTable
                striped
                bordered
                hover
                entries={10}
                pagesAmount={5}
                data={data}
                responsive
                theadColor="light"
                className="custom-mdb-table"
            />
        </div>
    );
}

function TodayRefundsTable({ refundData }) {
    if (!refundData || refundData.length === 0)
        return <p className="text-muted">No refund data available.</p>;

    const data = {
        columns: [
            // { label: "S.No", field: "sno", sort: "asc", width: 60 },
            { label: "Roll No.", field: "roll_no", sort: "asc", width: 100 },
            { label: "Name", field: "name", sort: "asc", width: 150 },
            { label: "Course", field: "course_name", sort: "asc", width: 150 },
            { label: "Technology", field: "technology_name", sort: "asc", width: 150 },
            { label: "Amount", field: "amount", sort: "asc", width: 100 },
            { label: "Reason", field: "reason", sort: "asc", width: 150 },
            { label: "Term", field: "term", sort: "asc", width: 100 },
            { label: "Payment ID", field: "payment_id", sort: "asc", width: 150 },
            { label: "Refund Date", field: "refund_date", sort: "asc", width: 130 },
            { label: "College", field: "college", sort: "asc", width: 120 },
            { label: "Branch", field: "branch", sort: "asc", width: 120 },
            { label: "Cashier Name", field: "cashier_name", sort: "asc", width: 150 },
            { label: "Cash Counter", field: "cash_counter", sort: "asc", width: 150 },
        ],

        rows: refundData.map((item, index) => ({
            sno: index + 1,
            roll_no: item.roll_no || "N/A",
            name: item.first_name || item.name || "N/A",
            course_name: item.course_name || " ",
            technology_name: item.technology_name || " ",
            amount: (
                <span className="text-danger fw-semibold">
                    {formatCurrency(item.refund_amount || item.amount || 0)}
                </span>
            ),

            reason: item.refund_reason || "N/A",
            term: item.term_name || item.term || "N/A",

            payment_id: (
                <span className="small text-muted">{item.payment_id}</span>
            ),

            refund_date: (
                <span className="small">
                    {formatDate(item.refund_given_date || item.refund_date)}
                </span>
            ),

            college: item.college || "N/A",

            branch: Array.isArray(item.branch)
                ? item.branch.join(", ")
                : item.branch || "N/A",

            cashier_name: item.cashier_name || item.refund_given_by || "N/A",

            cash_counter: item.refund_cashcounter || item.cash_counter_name || "N/A",
        })),
    };

    return (
        <div className="datatable-container fade-in">
            <MDBDataTable
                striped
                bordered
                hover
                entries={10}
                pagesAmount={5}
                data={data}
                responsive
                theadColor="light"
                className="custom-mdb-table"
            />
        </div>
    );
}




function TotalRefundsStatistics({ collegeNames }) {
    const [selectedStat, setSelectedStat] = useState(null);
    const [todayRefundData, setTodayRefundData] = useState(null);
    const [totalRefundData, setTotalRefundData] = useState(null);
    const [pageIsLoading, setPageIsLoading] = useState(true);
    const [pageError, setPageError] = useState(null);

    const [selectedDate, setSelectedDate] = useState("");
    const [dateRefundData, setDateRefundData] = useState(null);
    const [isDateLoading, setIsDateLoading] = useState(false);
    const [dateError, setDateError] = useState(null);

    const [selectedCollege, setSelectedCollege] = useState("All");
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const collegeList = collegeNames?.map((ele) => ele.college);

    useEffect(() => {
        const fetchAllData = async () => {
            setPageIsLoading(true);
            setPageError(null);

            const fetchTodayRefunds = api.get(`${backendUrl}get-overall-refund-data-today`);
            const fetchTotalRefunds = api.get(`${backendUrl}get-overall-refund-data`);

            try {
                const results = await Promise.allSettled([fetchTodayRefunds, fetchTotalRefunds]);
                let fetchError = null;

                if (results[0].status === "fulfilled" && results[0].value.status === 200) {
                    setTodayRefundData(results[0].value.data);
                } else {
                    fetchError = "Could not fetch Today's refund data.";
                }

                if (results[1].status === "fulfilled" && results[1].value.status === 200) {
                    setTotalRefundData(results[1].value.data);
                } else {
                    fetchError = (fetchError || "") + " Could not fetch Total refund data.";
                }

                if (fetchError) setPageError(fetchError);
            } catch {
                setPageError("An unexpected error occurred while fetching data.");
            } finally {
                setPageIsLoading(false);
            }
        };
        fetchAllData();
    }, [backendUrl]);

    const handleCollegeChange = async (e) => {
        const newCollege = e.target.value;
        setSelectedCollege(newCollege);
        setSelectedStat(null);
        setDateRefundData(null);
        setDateError(null);
        setPageIsLoading(true);
        setPageError(null);

        let fetchTodayRefunds, fetchTotalRefunds;
        try {
            if (newCollege === "All") {
                fetchTodayRefunds = api.get(`${backendUrl}get-overall-refund-data-today`);
                fetchTotalRefunds = api.get(`${backendUrl}get-overall-refund-data`);
            } else {
                fetchTodayRefunds = api.post(`${backendUrl}get-overall-refund-data-by-cashcounter-name-today`, {
                    refund_cashcounter: newCollege,
                });
                fetchTotalRefunds = api.post(`${backendUrl}get-overall-refund-data-by-cashcounter-name-total`, {
                    refund_cashcounter: newCollege,
                });
            }

            const results = await Promise.allSettled([fetchTodayRefunds, fetchTotalRefunds]);
            let fetchError = null;

            if (results[0].status === "fulfilled" && results[0].value.status === 200) {
                setTodayRefundData(results[0].value.data);
            } else {
                fetchError = `Could not fetch Today's data for ${newCollege}.`;
            }

            if (results[1].status === "fulfilled" && results[1].value.status === 200) {
                setTotalRefundData(results[1].value.data);
            } else {
                fetchError = (fetchError || "") + ` Could not fetch Total data for ${newCollege}.`;
            }

            if (fetchError) setPageError(fetchError);
        } catch {
            setPageError("An unexpected error occurred while fetching filtered data.");
        } finally {
            setPageIsLoading(false);
        }
    };

    const handleDateChange = async (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);

        if (!newDate) {
            setDateRefundData(null);
            setDateError(null);
            return;
        }

        setIsDateLoading(true);
        setDateError(null);
        setDateRefundData(null);

        try {
            const body = { date: newDate };
            if (selectedCollege !== "All") body.refund_cashcounter = selectedCollege;

            const response = await api.post(`${backendUrl}get-overall-refund-data-date-wise`, body);

            if (response.status === 200 && response.data.success) {
                setDateRefundData(response.data);
                setSelectedStat("byDate");
            } else {
                setDateError("No data found for this date.");
                setDateRefundData(null);
            }
        } catch {
            setDateError("Failed to fetch data.");
            setDateRefundData(null);
        } finally {
            setIsDateLoading(false);
        }
    };

    const stats = [
        {
            id: "today",
            title: "Today's Refunds",
            amount: todayRefundData?.total_refund_amount || 0,
            count: todayRefundData?.count || 0,
            icon: ZapIcon,
            iconBg: "bg-success-subtle",
            iconColor: "text-success",
        },
        {
            id: "total",
            title: "Total Refunds",
            amount: totalRefundData?.total_refund_amount || 0,
            count: totalRefundData?.count || 0,
            icon: ReceiptIcon,
            iconBg: "bg-primary-subtle",
            iconColor: "text-primary",
        },
    ];

    if (pageIsLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="p-4 p-md-5">
                <div className="alert alert-danger" role="alert">
                    <h4 className="alert-heading">Error!</h4>
                    <p>Failed to load statistics. Please try again later.</p>
                    <hr />
                    <p className="mb-0 small">{pageError}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-0 refunds-statistics">

            {/* --- STAT CARDS ROW (Now includes Filter Card) --- */}
            <div className="row g-4 mb-4">

                {/* --- NEW: Filter Card --- */}
                <div className="col-lg-3 col-md-6">
                    <div className="bg-white rounded-4 shadow-sm p-4 h-100">
                        <div>
                            <h6 className="fw-bold text-dark mb-1">Filter Refund Data</h6>
                            <p className="text-muted small mb-3">Select a college to view its refund summary and reports</p>
                        </div>
                        <div className="filter-control">
                            <label
                                htmlFor="collegeFilterRefunds"
                                className="form-label fw-semibold text-muted mb-1 small"
                            >
                                College:
                            </label>
                            <select
                                id="collegeFilterRefunds"
                                className="form-select modern-select"
                                value={selectedCollege}
                                onChange={handleCollegeChange}
                            >
                                <option value="All">All Cashcounters</option>
                                {collegeList?.map((college) => (
                                    <option key={college} value={college}>
                                        {college}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* --- Stat Cards --- */}
                {stats.map((s) => (
                    <div className="col-lg-3 col-md-6" key={s.id}>
                        <StatCard
                            title={s.title}
                            amount={s.amount}
                            count={s.count}
                            Icon={s.icon}
                            iconBg={s.iconBg}
                            iconColor={s.iconColor}
                            isSelected={selectedStat === s.id}
                            onClick={() => setSelectedStat(s.id === selectedStat ? null : s.id)}
                        />
                    </div>
                ))}

                {/* --- Date Card (Conditional) --- */}
                {selectedCollege === "All" && (
                    <div className="col-lg-3 col-md-6">
                        <DateStatCard
                            title="Refunds by Date"
                            amount={dateRefundData?.total_amount || 0}
                            count={dateRefundData?.count || 0}
                            selectedDate={selectedDate}
                            onDateChange={handleDateChange}
                            isLoading={isDateLoading}
                            error={dateError}
                            isSelected={selectedStat === "byDate"}
                            data={dateRefundData}
                            onClick={() => {
                                if (dateRefundData) setSelectedStat(selectedStat === "byDate" ? null : "byDate");
                            }}
                        />
                    </div>
                )}
            </div>

            {/* --- DETAILS SECTION --- */}
            <div className="mt-2">
                <DetailBox
                    title={
                        selectedStat === "total"
                            ? "All Refunds"
                            : selectedStat === "today"
                                ? "Today's Refunds"
                                : selectedStat === "byDate"
                                    ? `Refunds for ${(formatDateWithoutTime(selectedDate))}`
                                    : "Details"
                    }
                >
                    {selectedStat === "total" && <RefundsTable refundData={totalRefundData?.data || []} />}
                    {selectedStat === "today" && <RefundsTable refundData={todayRefundData?.data || []} />}
                    {selectedStat === "byDate" && <RefundsTable refundData={dateRefundData?.data || []} />}

                    {!selectedStat && (
                        <div className="text-center border-dashed p-5 rounded-3">
                            <p className="text-muted fw-medium mb-0">
                                Click on a card to see more details.
                            </p>
                        </div>
                    )}
                </DetailBox>
            </div>
        </div>
    );
}


function PaymentsTable({ paymentsData }) {
    if (!paymentsData || paymentsData.length === 0)
        return <p className="text-muted">No payment data available.</p>;
    // console.log(paymentsData)
    const data = {
        columns: [
            { label: "S.No", field: "sno", sort: "asc", width: 60 },
            { label: "Roll No.", field: "roll_no", sort: "asc", width: 100 },
            { label: "Name", field: "name", sort: "asc", width: 150 },
            { label: "Course", field: "course_name", sort: "asc", width: 150 },
            { label: "Technology", field: "technology_name", sort: "asc", width: 150 },
            { label: "Amount", field: "amount", sort: "asc", width: 100 },
            { label: "Discount", field: "discount", sort: "asc", width: 100 },
            { label: "Term", field: "term", sort: "asc", width: 100 },
            { label: "Payment ID", field: "payment_id", sort: "asc", width: 150 },
            { label: "Payment Date", field: "payment_date", sort: "asc", width: 130 },
            { label: "College", field: "college", sort: "asc", width: 100 },
            { label: "Branch", field: "branch", sort: "asc", width: 120 },
            { label: "Cashier Name", field: "cashier_name", sort: "asc", width: 120 },
            { label: "Cash Counter", field: "cash_counter", sort: "asc", width: 120 },
        ],

        rows: paymentsData.map((item, index) => ({
            sno: index + 1,
            roll_no: item.roll_no || "N/A",
            name: item.name || item.first_name || "N/A",
            course_name: item.course_name || "",
            technology_name: item.technology_name || "",
            amount: (
                <span className="text-success fw-semibold">
                    {formatCurrency(item.amount)}
                </span>
            ),
            discount: (
                <span className="text-success fw-semibold">
                    {formatCurrency(item.discount)}
                </span>
            ),

            term: item.term_name || item.term || "N/A",

            payment_id: (
                <span className="text-muted small">{item.payment_id || "N/A"}</span>
            ),

            payment_date: (
                <span className="small">{formatDate(item.payment_date)}</span>
            ),

            college: item.college || "N/A",

            branch: Array.isArray(item.branch)
                ? item.branch.join(", ")
                : item.branch || "N/A",

            cashier_name: item.cashier_name || "N/A",

            cash_counter: item.cash_counter_name || "N/A",
        })),
    };

    return (
        <div className="datatable-container fade-in">
            <MDBDataTable
                striped
                bordered
                hover
                entries={10}
                pagesAmount={5}
                data={data}
                responsive
                theadColor="light"
                className="custom-mdb-table"
            />
        </div>
    );
}



function TodaySummaryCard({ summary, isSelected, onClick }) {
    return (
        <div
            onClick={onClick}
            className={`summary-card ${isSelected ? "active" : ""}`}
        >
            <div className="summary-card-header">
                <div>
                    <h6 className="fw-bold text-dark mb-1">Today's Summary</h6>
                    <p className="text-muted small mb-0">Daily net performance overview</p>
                </div>
                <div className="summary-icon bg-gradient-success">
                    <ZapIcon className="icon-md text-white" />
                </div>
            </div>

            {summary ? (
                <div className="summary-body mt-3">
                    <h4 className="fw-bold text-dark mb-1">
                        {/* {formatCurrency(
                            summary.net_amount_in_cashcounter ??
                            summary.net_amount ??
                            (summary.total_payments_today ?? summary.total_payments_amount ?? 0) -
                            (summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)
                        )} */}
                        {formatCurrency(
                            summary.total_payments_today ??
                            summary.total_payments_amount ??
                            0
                        )}
                        <span className="h6 text-muted fw-normal ms-2">(Net)</span>
                    </h4>
                    <div className="d-flex justify-content-between small mt-2">
                        <span className="text-success fw-semibold">
                            Payments: {formatCurrency(summary.total_payments_today ?? summary.total_payments_amount ?? 0)} (
                            {summary.total_payments_count ?? 0})
                        </span>
                        <span className="text-danger fw-semibold">
                            Refunds: {formatCurrency(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)} (
                            {summary.total_refunds_count ?? 0})
                        </span>
                    </div>
                </div>
            ) : (
                <p className="h6 text-muted mt-3 mb-0">Select a card to view today's summary.</p>
            )}
        </div>
    );
}

function TotalSummaryCard({ summary, isSelected, onClick }) {
    return (
        <div
            onClick={onClick}
            className={`summary-card ${isSelected ? "active" : ""}`}
        >
            <div className="summary-card-header">
                <div>
                    <h6 className="fw-bold text-dark mb-1">Total Summary</h6>
                    <p className="text-muted small mb-0">Overall financial performance</p>
                </div>
                <div className="summary-icon bg-gradient-primary">
                    <ReceiptIcon className="icon-md text-white" />
                </div>
            </div>

            {summary ? (
                <div className="summary-body mt-3">
                    <h4 className="fw-bold text-dark mb-1">
                        {formatCurrency(
                            summary.net_amount_in_cashcounter ??
                            summary.net_amount ??
                            (summary.total_payments_amount ?? 0) - (summary.total_refunds_amount ?? 0)
                        )}
                        <span className="h6 text-muted fw-normal ms-2">(Net)</span>
                    </h4>
                    <div className="d-flex justify-content-between small mt-2">
                        <span className="text-success fw-semibold">
                            Payments: {formatCurrency(summary.total_payments_amount ?? 0)} (
                            {summary.total_payments_count ?? 0})
                        </span>
                        <span className="text-danger fw-semibold">
                            Refunds: {formatCurrency(summary.total_refunds_amount ?? 0)} (
                            {summary.total_refunds_count ?? 0})
                        </span>
                    </div>
                </div>
            ) : (
                <p className="h6 text-muted mt-3 mb-0">Total summary not available.</p>
            )}
        </div>
    );
}

function TodaySummaryModal({ show, onClose, summaryData, college }) {
    const handleExcelDownload = () => {
        if (typeof window.XLSX === "undefined") {
            console.error("XLSX library is not loaded. Cannot download Excel.");
            return;
        }
        try {
            const summary = summaryData?.summary || {};
            const collegeName = college === "All" ? "All Cashiers" : college;
            const today = formatDate(new Date().toISOString().split("T")[0]);

            // let ws_data = [
            //     ["Today's Summary Report"],
            //     ["Date:", today],
            //     ["Scope:", collegeName],
            //     [],
            //     ["Today's Payments"],
            //     // ["S.No", "Roll No.", "Name", "Amount", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier Name", "Cash Counter"],
            //     ["S.No", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier Name", "Cash Counter", "Amount"],
            // ];
            // let ws_data = [
            //     ["Today's Summary Report"],
            //     ["Date:", today],
            //     ["Scope:", collegeName],
            //     [],
            //     [`Total Thub Payments from ${today} to ${today}`],
            //     ["S.No", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier Name", "Cash Counter", "Amount"],
            // ];
            let ws_data = [];
            const merges = [];

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push(["Today's Summary Report"]);
            ws_data.push(["Date:", today]);
            ws_data.push(["Scope:", collegeName]);
            ws_data.push([]);

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Total Thub Payments from ${today} to ${today}`]);
            ws_data.push(["S.No", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier Name", "Cash Counter", "Amount"]);

            const payments = summaryData?.today_payments || [];
            // payments.forEach((item, index) => {
            //     ws_data.push([
            //         index + 1,
            //         item.roll_no || "N/A",
            //         item.name || item.first_name || "N/A",
            //         item.amount || 0,
            //         item.term_name || "N/A",
            //         item.payment_id || "N/A",
            //         formatDate(item.payment_date),
            //         item.college || "N/A",
            //         Array.isArray(item.branch) ? item.branch.join(", ") : item.branch || "N/A",
            //         item.cashier_name || "N/A",
            //         item.cash_counter_name || "N/A",
            //     ]);
            // });
            payments.forEach((item, index) => {
                ws_data.push([
                    index + 1,
                    item.roll_no || "N/A",
                    item.name || item.first_name || "N/A",
                    item.term_name || "N/A",
                    item.payment_id || "N/A",
                    formatDate(item.payment_date),
                    item.college || "N/A",
                    Array.isArray(item.branch) ? item.branch.join(", ") : item.branch || "N/A",
                    item.cashier_name || "N/A",
                    item.cash_counter_name || "N/A",
                    item.amount || 0,
                ]);
            });
            // ws_data.push(["", "Total Payments:", summary.total_payments_today ?? summary.total_payments_amount ?? 0, "", "", "", "", ""]);
            // ws_data.push(["", "", "Total Payments:", summary.total_payments_today ?? summary.total_payments_amount ?? 0, "", "", "", "", "", "", ""]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Total Payments:", summary.total_payments_today ?? summary.total_payments_amount ?? 0]);
            ws_data.push([]);
            ws_data.push([]);

            // ws_data.push(["Today's Refunds"]);
            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Total Thub Refunds from ${today} to ${today}`]);            // ws_data.push(["S.No", "Roll No.", "Name", "Amount", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier Name", "Cash Counter"]);
            ws_data.push(["S.No", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier Name", "Cash Counter", "Amount"]); const refunds = summaryData?.today_refunds || [];
            refunds.forEach((item, index) => {
                ws_data.push([
                    index + 1,
                    item.roll_no || "N/A",
                    item.first_name || item.name || "N/A",
                    item.refund_reason || "N/A",
                    item.term_name || "N/A",
                    item.payment_id || "N/A",
                    formatDate(item.refund_date),
                    item.college || "N/A",
                    item.cashier_name || "N/A",
                    item.cash_counter_name || "N/A",
                    item.amount || 0,
                ]);
            });
            // ws_data.push(["", "Total Refunds:", summary.total_refunds_today ?? summary.total_refunds_amount ?? 0, "", "", "", "", ""]);
            // ws_data.push(["", "", "Total Refunds:", summary.total_refunds_today ?? summary.total_refunds_amount ?? 0, "", "", "", "", "", "", ""]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Total Refunds:", summary.total_refunds_today ?? summary.total_refunds_amount ?? 0]);
            ws_data.push([]);
            ws_data.push([]);

            ws_data.push(["Overall Summary"]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Metric", "Value"]);
            ws_data.push([
                "", "", "", "", "", "", "", "", "",
                "Total Payments",
                `${formatCurrency(summary.total_payments_today ?? summary.total_payments_amount ?? 0)} (${summary.total_payments_count ?? 0} records)`,
            ]);
            ws_data.push([
                "", "", "", "", "", "", "", "", "",
                "Total Refunds",
                `${formatCurrency(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)} (${summary.total_refunds_count ?? 0} records)`,
            ]);
            // ws_data.push([
            //     "", "", "", "", "", "", "", "", "",
            //     "Net Amount",
            //     cleanText(formatCurrency(
            //         summary.net_amount_in_cashcounter ??
            //         summary.net_amount ??
            //         (summary.total_payments_today ?? summary.total_payments_amount ?? 0) -
            //         (summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)
            //     )),
            // ]);

            const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
            ws['!merges'] = merges;
            const wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, "Today Summary");
            window.XLSX.writeFile(wb, `Today_Summary_${collegeName}_${today}.xlsx`);
        } catch (err) {
            console.error("Error creating Excel file:", err);
        }
    };
    const handlePdfDownload = () => {
        if (!summaryData) return;

        const summary = summaryData.summary || {};
        const collegeName = college === "All" ? "All Cashiers" : college;
        const today = formatDate(new Date().toISOString().split("T")[0]);

        const doc = new jsPDF("landscape");

        // Title
        doc.setFontSize(16);
        doc.text(`Today's Summary Report`, 14, 15);

        // Date & Scope
        doc.setFontSize(11);
        doc.text(`Date: ${today}`, 14, 25);
        doc.text(`Scope: ${collegeName}`, 14, 32);

        // ============== Payments Table =================
        autoTable(doc, {
            startY: 40,
            head: [["S.No", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier", "Cash Counter", "Amount"]],
            body: (summaryData.today_payments || []).map((item, index) => [
                index + 1,
                item.roll_no || "N/A",
                item.name || item.first_name || "N/A",
                item.term_name || "N/A",
                item.payment_id || "N/A",
                formatDate(item.payment_date),
                item.college || "N/A",
                Array.isArray(item.branch) ? item.branch.join(", ") : item.branch || "N/A",
                item.cashier_name || "N/A",
                item.cash_counter_name || "N/A",
                item.amount || 0,
            ]),
            styles: { fontSize: 9 },
            columnStyles: { 10: { halign: "right" } }
        });

        // Total Payments (underline row like in Excel)
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 5,
            body: [["Total Payments", summary.total_payments_today ?? summary.total_payments_amount ?? 0]],
            theme: "grid",
            styles: { fontSize: 10 },
            columnStyles: { 0: { halign: "left" }, 1: { halign: "right" } }
        });

        // ============== Refunds Table =================
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 12,
            head: [["S.No", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier", "Cash Counter", "Amount"]],
            body: (summaryData.today_refunds || []).map((item, index) => [
                index + 1,
                item.roll_no || "N/A",
                item.first_name || item.name || "N/A",
                item.refund_reason || "N/A",
                item.term_name || "N/A",
                item.payment_id || "N/A",
                formatDate(item.refund_date),
                item.college || "N/A",
                item.cashier_name || "N/A",
                item.cash_counter_name || "N/A",
                item.amount || 0,
            ]),
            styles: { fontSize: 9 },
            columnStyles: { 10: { halign: "right" } }
        });

        // Total Refunds
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 5,
            body: [["Total Refunds", summary.total_refunds_today ?? summary.total_refunds_amount ?? 0]],
            theme: "grid",
            styles: { fontSize: 10 },
            columnStyles: { 0: { halign: "left" }, 1: { halign: "right" } }
        });

        // ============== Overall Summary =================
        //         autoTable(doc, {
        //     startY: doc.lastAutoTable.finalY + 12,
        //     head: [["Metric", "Value"]],
        //     body: [
        //         ["Total Payments", formatCurrencyPdf(summary.total_payments_today ?? summary.total_payments_amount ?? 0)],
        //         ["Total Refunds", formatCurrencyPdf(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)],
        //         [
        //             "Net Amount",
        //             formatCurrencyPdf(
        //                 summary.net_amount_in_cashcounter ??
        //                 summary.net_amount ??
        //                 (summary.total_payments_today ?? summary.total_payments_amount ?? 0) -
        //                 (summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)
        //             )
        //         ],
        //     ],
        //     styles: { fontSize: 10 },
        //     columnStyles: { 1: { halign: "right" } }
        // });
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 12,
            head: [["Metric", "Value"]],
            body: [
                ["Total Payments", formatCurrencyPdf(summary.total_payments_today ?? summary.total_payments_amount ?? 0)],
                ["Total Refunds", formatCurrencyPdf(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)],
            ],
            styles: { fontSize: 10 },
            columnStyles: { 1: { halign: "right" } }
        });

        doc.save(`Today_Summary_${collegeName}_${today}.pdf`);
    };
    const cleanText = (value) => {
        if (!value) return "";
        return value
            .toString()
            .replace(/[\u200B-\u200F\u202A-\u202E]/g, "") // remove invisible unicode chars
            .trim();
    };

    return (
        <div className={`modal fade ${show ? "show d-block" : ""}`} tabIndex="-1" style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "transparent" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title h4">Today's Full Summary</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <h3 className="h5">Summary</h3>
                        {summaryData?.summary ? (
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Net Amount</div>
                                        <div className="h4 fw-bold">
                                            {/* {formatCurrency(
                                                summaryData.summary.net_amount_in_cashcounter ??
                                                summaryData.summary.net_amount ??
                                                (summaryData.summary.total_payments_today ?? summaryData.summary.total_payments_amount ?? 0) -
                                                (summaryData.summary.total_refunds_today ?? summaryData.summary.total_refunds_amount ?? 0)
                                            )} */}
                                             {formatCurrency(
        summaryData.summary.total_payments_today ??
        summaryData.summary.total_payments_amount ??
        0
    )}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Total Payments</div>
                                        <div className="h4 fw-bold text-success">
                                            {formatCurrency(summaryData.summary.total_payments_today ?? summaryData.summary.total_payments_amount ?? 0)} (
                                            {summaryData.summary.total_payments_count ?? 0})
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Total Refunds</div>
                                        <div className="h4 fw-bold text-danger">
                                            {formatCurrency(summaryData.summary.total_refunds_today ?? summaryData.summary.total_refunds_amount ?? 0)} (
                                            {summaryData.summary.total_refunds_count ?? 0})
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <p>No summary available.</p>
                        )}

                        <h3 className="h5 mt-4">Today's Payments</h3>
                        <PaymentsTable paymentsData={summaryData?.today_payments || []} />

                        <h3 className="h5 mt-4">Today's Refunds</h3>
                        <TodayRefundsTable refundData={summaryData?.today_refunds || []} />
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                        <button type="button" className="btn btn-success" onClick={handleExcelDownload} disabled={!window.XLSX}>
                            Download Excel
                        </button>
                        <button className="btn btn-danger" onClick={handlePdfDownload}>Download PDF</button>

                    </div>
                </div>
            </div>
        </div>
    );
}

function DateSummaryModal({ show, onClose, summaryData, date }) {
    const cleanText = (value) => {
        if (!value) return "";
        return value.toString().replace(/[\u200B-\u200F\u202A-\u202E]/g, "").trim();
    };

    const handleExcelDownload = () => {
        if (typeof window.XLSX === "undefined") {
            console.error("XLSX library not loaded.");
            return;
        }

        try {
            const summary = summaryData?.summary || {};
            const finalDate = formatDate(date);

            // let ws_data = [[`Full Report for ${finalDate}`], [], ["Payments"], ["S.no", "Roll No.", "Name", "Amount", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier Name", "Cash Counter"]];
            // let ws_data = [[`Full Report for ${finalDate}`], [], ["Payments"], ["S.no", "Roll No.", "Name", "Course", "Technology", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier Name", "Cash Counter", "Amount"]];
            let ws_data = [];
            const merges = [];

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Full Report for ${finalDate}`]);
            ws_data.push([]);

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Total Thub Payments from ${finalDate} to ${finalDate}`]);
            ws_data.push(["S.no", "Roll No.", "Name", "Course", "Technology", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier Name", "Cash Counter", "Amount"]);
            const payments = summaryData?.today_payments || [];
            payments.forEach((item, index) => {
                ws_data.push([
                    index + 1,
                    item.roll_no || "N/A",
                    item.name || item.first_name || "N/A",
                    item.course_name || " ",
                    item.technology_name || " ",
                    item.term_name || "N/A",
                    item.payment_id || "N/A",
                    formatDate(item.payment_date),
                    item.college || "N/A",
                    Array.isArray(item.branch) ? item.branch.join(", ") : item.branch || "N/A",
                    item.cashier_name || "N/A",
                    item.cash_counter_name || "N/A",
                    item.amount || 0,
                ]);
            });

            ws_data.push([]);
            // ws_data.push(["Refunds"]);
            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push([`Total Thub Refunds from ${finalDate} to ${finalDate}`]);            // ws_data.push(["S.no", "Roll No.", "Name", "Amount", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier Name", "Cash Counter"]);
            ws_data.push(["S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier Name", "Cash Counter", "Amount"]);
            const refunds = summaryData?.today_refunds || [];
            refunds.forEach((item, index) => {
                ws_data.push([
                    index + 1,
                    item.roll_no || "N/A",
                    item.first_name || item.name || "N/A",
                    item.refund_reason || "N/A",
                    item.term_name || "N/A",
                    item.payment_id || "N/A",
                    formatDate(item.refund_date),
                    item.college || "N/A",
                    item.cashier_name || "N/A",
                    item.cash_counter_name || "N/A",
                    item.amount || 0,
                ]);
            });

            ws_data.push([]);
            ws_data.push([]);
            ws_data.push(["Summary"]);
            ws_data.push([
                "", "", "", "", "", "", "", "", "", "",
                "Total Payments",
                `${formatCurrency(summary.total_payments_today ?? summary.total_payments_amount ?? 0)} (${summary.total_payments_count ?? 0} records)`,
            ]);
            ws_data.push([
                "", "", "", "", "", "", "", "", "", "",
                "Total Refunds",
                `${formatCurrency(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)} (${summary.total_refunds_count ?? 0} records)`,
            ]);
            // ws_data.push([
            //     "", "", "", "", "", "", "", "", "", "",
            //     "Net Amount",
            //     formatCurrency(
            //         summary.net_amount_in_cashcounter ??
            //         summary.net_amount ??
            //         (summary.total_payments_today ?? summary.total_payments_amount ?? 0) -
            //         (summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)
            //     ),
            // ]);
            const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
            ws['!merges'] = merges;
            const wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, "Date Summary");
            window.XLSX.writeFile(wb, `Date_Summary_${finalDate}.xlsx`);
        } catch (err) {
            console.error("Excel error:", err);
        }
    };
    const handlePdfDownload = () => {
        if (!summaryData) return;

        const summary = summaryData.summary || {};
        const finalDate = formatDate(date);

        const doc = new jsPDF("landscape");

        doc.setFontSize(16);
        doc.text(`Full Report for ${finalDate}`, 14, 15);

        // ========== Payments Table ==========
        autoTable(doc, {
            startY: 30,
            head: [["S.No", "Roll No.", "Name", "Course", "Technology", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier", "Cash Counter", "Amount"]],
            body: (summaryData.today_payments || []).map((item, index) => [
                index + 1,
                item.roll_no || "N/A",
                item.name || item.first_name || "N/A",
                item.course_name || "N/A",
                item.technology_name || "N/A",
                item.term_name || "N/A",
                item.payment_id || "N/A",
                formatDate(item.payment_date),
                item.college || "N/A",
                Array.isArray(item.branch) ? item.branch.join(", ") : item.branch || "N/A",
                item.cashier_name || "N/A",
                item.cash_counter_name || "N/A",
                item.amount || 0,
            ]),
            styles: { fontSize: 9 },
            columnStyles: { 12: { halign: "right" } }
        });

        // Total Payments
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 4,
            body: [["Total Payments", summary.total_payments_count ?? 0, cleanText(formatCurrencyPdf(summary.total_payments_today ?? summary.total_payments_amount ?? 0))]],
            theme: "grid",
            styles: { fontSize: 10 },
            columnStyles: { 0: { halign: "left" }, 1: { halign: "left" }, 2: { halign: "right" } }
        });

        // ========== Refunds Table ==========
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 12,
            head: [["S.No", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier", "Cash Counter", "Amount"]],
            body: (summaryData.today_refunds || []).map((item, index) => [
                index + 1,
                item.roll_no || "N/A",
                item.first_name || item.name || "N/A",
                item.refund_reason || "N/A",
                item.term_name || "N/A",
                item.payment_id || "N/A",
                formatDate(item.refund_date),
                item.college || "N/A",
                item.cashier_name || "N/A",
                item.cash_counter_name || "N/A",
                item.amount || 0,
            ]),
            styles: { fontSize: 9 },
            columnStyles: { 10: { halign: "right" } }
        });

        // Total Refunds
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 4,
            body: [["Total Refunds", summary.total_refunds_count ?? 0, cleanText(formatCurrencyPdf(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0))]],
            theme: "grid",
            styles: { fontSize: 10 },
            columnStyles: { 0: { halign: "left" }, 1: { halign: "left" }, 2: { halign: "right" } }
        });

        // ========== Summary Metrics ==========
        //         autoTable(doc, {
        //     startY: doc.lastAutoTable.finalY + 12,
        //     head: [["Metric", "Value"]],
        //     body: [
        //         ["Total Payments", cleanText(formatCurrencyPdf(summary.total_payments_today ?? summary.total_payments_amount ?? 0))],
        //         ["Total Refunds", cleanText(formatCurrencyPdf(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0))],
        //         [
        //             "Net Amount",
        //             cleanText(
        //                 formatCurrencyPdf(
        //                     summary.net_amount_in_cashcounter ??
        //                     summary.net_amount ??
        //                     (summary.total_payments_today ?? summary.total_payments_amount ?? 0) -
        //                     (summary.total_refunds_today ?? summary.total_refunds_amount ?? 0)
        //                 )
        //             )
        //         ],
        //     ],
        //     styles: { fontSize: 10 },
        //     columnStyles: { 1: { halign: "right" } }
        // });
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 12,
            head: [["Metric", "Value"]],
            body: [
                ["Total Payments", cleanText(formatCurrencyPdf(summary.total_payments_today ?? summary.total_payments_amount ?? 0))],
                ["Total Refunds", cleanText(formatCurrencyPdf(summary.total_refunds_today ?? summary.total_refunds_amount ?? 0))],
            ],
            styles: { fontSize: 10 },
            columnStyles: { 1: { halign: "right" } }
        });
        doc.save(`Date_Summary_${finalDate}.pdf`);
    };


    return (
        <div className={`modal fade ${show ? "show d-block" : ""}`} tabIndex="-1" style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "transparent" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title h4">Full Report for {formatDate(date)}</h5>
                        <button className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body">
                        <h4 className="fw-bold">Summary</h4>
                        {summaryData?.summary ? (
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Net Amount</div>
                                        <div className="h4 fw-bold">
                                            {/* {formatCurrency(
                                                summaryData.summary.net_amount_in_cashcounter ??
                                                summaryData.summary.net_amount ??
                                                (summaryData.summary.total_payments_today ?? summaryData.summary.total_payments_amount ?? 0) -
                                                (summaryData.summary.total_refunds_today ?? summaryData.summary.total_refunds_amount ?? 0)
                                            )} */}
                                            <div className="small text-muted">Net Amount</div>
    {formatCurrency(
        summaryData.summary.total_payments_today ??
        summaryData.summary.total_payments_amount ??
        0
    )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Total Payments</div>
                                        <div className="h4 fw-bold text-success">
                                            {formatCurrency(summaryData.summary.total_payments_today ?? summaryData.summary.total_payments_amount ?? 0)} (
                                            {summaryData.summary.total_payments_count ?? 0})
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Total Refunds</div>
                                        <div className="h4 fw-bold text-danger">
                                            {formatCurrency(summaryData.summary.total_refunds_today ?? summaryData.summary.total_refunds_amount ?? 0)} (
                                            {summaryData.summary.total_refunds_count ?? 0})
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            "No summary"
                        )}

                        <h4 className="fw-bold mt-4">Payments</h4>
                        <PaymentsTable paymentsData={summaryData?.today_payments || []} />

                        <h4 className="fw-bold mt-4">Refunds</h4>
                        <TodayRefundsTable refundData={summaryData?.today_refunds || []} />
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>
                            Close
                        </button>
                        <button className="btn btn-success" onClick={handleExcelDownload}>
                            Download Excel
                        </button>
                        <button className="btn btn-danger" onClick={handlePdfDownload}>Download PDF</button>

                    </div>
                </div>
            </div>
        </div>
    );
}

function TotalSummaryModal({ show, onClose, summaryData, cashier }) {
    const cleanText = (value) => {
        if (!value) return "";
        return value.toString().replace(/[\u200B-\u200F\u202A-\u202E]/g, "").trim();
    };

    const handleExcelDownload = () => {
        if (!window.XLSX) return alert("XLSX not loaded!");

        try {
            const summary = summaryData?.summary || {};
            const title = cashier === "All" ? "All Cashiers" : cashier;

            const ws_data = [];
            const merges = [];

            merges.push({ s: { r: ws_data.length, c: 3 }, e: { r: ws_data.length, c: 5 } });
            ws_data.push(["", "", "", `Full Total Summary Report — ${title}`]);
            ws_data.push([]);

            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push(["Total Thub Payments"]);
            ws_data.push(["S.no", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier Name", "Cash Counter", "Amount"]);
            (summaryData?.all_payments || []).forEach((p, index) => {
                ws_data.push([
                    index + 1,
                    p.roll_no || "N/A",
                    p.name || p.first_name || "N/A",
                    p.term_name || "N/A",
                    p.payment_id || "N/A",
                    formatDate(p.payment_date),
                    p.college || "N/A",
                    Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
                    p.cashier_name || "N/A",
                    p.cash_counter_name || "N/A",
                    p.amount || 0,
                ]);
            });
            ws_data.push([]);
            // ws_data.push(["Refunds"]);
            merges.push({ s: { r: ws_data.length, c: 0 }, e: { r: ws_data.length, c: 2 } });
            ws_data.push(["Total Thub Refunds"]);            // ws_data.push(["S.no", "Roll No.", "Name", "Amount", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier Name", "Cash Counter"]);
            ws_data.push(["S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier Name", "Cash Counter", "Amount"]);
            (summaryData?.refunds_given || []).forEach((r, index) => {
                ws_data.push([
                    index + 1,
                    r.roll_no || "N/A",
                    r.name || r.first_name || "N/A",
                    r.refund_reason || "N/A",
                    r.term_name || "N/A",
                    r.payment_id || "N/A",
                    formatDate(r.refund_date),
                    r.college || "N/A",
                    r.cashier_name || "N/A",
                    r.cash_counter_name || "N/A",
                    r.amount || 0,
                ]);
            });
            ws_data.push([]);
            ws_data.push(["Summary"]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Total Payments", formatCurrency(summary.total_payments_amount || 0)]);
            ws_data.push(["", "", "", "", "", "", "", "", "", "Total Refunds", formatCurrency(summary.total_refunds_amount || 0)]);
            // ws_data.push(["", "", "", "", "", "", "", "", "", "Net Amount",
            //     formatCurrency(
            //         summary.net_amount_in_cashcounter ??
            //         summary.net_amount ??
            //         (summary.total_payments_amount || 0) -
            //         (summary.total_refunds_amount || 0)
            //     ),
            // ]);

            const ws = window.XLSX.utils.aoa_to_sheet(ws_data);
            ws['!merges'] = merges;

            // ✅ Set column widths (reduce width for S.no column)
            ws['!cols'] = [
                { wch: 2 },  // S.no (narrow)
                { wch: 12 }, // Roll No.
                { wch: 20 }, // Name
                { wch: 12 }, // Term
                { wch: 18 }, // Payment ID
                { wch: 14 }, // Payment Date
                { wch: 15 }, // College
                { wch: 20 }, // Branch
                { wch: 15 }, // Cashier Name
                { wch: 15 }, // Cash Counter
                { wch: 12 }, // Amount
            ];

            const wb = window.XLSX.utils.book_new();
            window.XLSX.utils.book_append_sheet(wb, ws, "Total Summary");
            window.XLSX.writeFile(wb, `Total_Summary_${title}.xlsx`);
        } catch (err) {
            console.error(err);
        }
    };
    const handlePdfDownload = () => {
        if (!summaryData) return;

        const summary = summaryData.summary || {};
        const title = cashier === "All" ? "All Cashiers" : cashier;

        const doc = new jsPDF("landscape");

        // Title
        doc.setFontSize(16);
        doc.text(`Full Total Summary Report — ${title}`, 14, 15);

        // =============== Payments Table ==================
        // =============== Payments Table ==================
        // =============== Payments Table ==================
        autoTable(doc, {
            startY: 28,
            head: [["S.no", "Roll No.", "Name", "Term", "Payment ID", "Payment Date", "College", "Branch", "Cashier", "Cash Counter", "Amount"]],
            body: (summaryData.all_payments || []).map((p, index) => [
                index + 1,
                p.roll_no || "N/A",
                p.name || p.first_name || "N/A",
                p.term_name || "N/A",
                p.payment_id || "N/A",
                formatDate(p.payment_date),
                p.college || "N/A",
                Array.isArray(p.branch) ? p.branch.join(", ") : p.branch || "N/A",
                p.cashier_name || "N/A",
                p.cash_counter_name || "N/A",
                cleanText(formatCurrency(p.amount || 0)),
            ]),
            styles: { fontSize: 9 },
            columnStyles: { 10: { halign: "right" } }
        });

        // Total Payments
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 5,
            body: [["Total Payments", cleanText(formatCurrencyPdf(summary.total_payments_amount ?? 0))]],
            theme: "grid",
            styles: { fontSize: 10 },
            columnStyles: { 0: { halign: "left" }, 1: { halign: "right" } }
        });

        // =============== Refunds Table ==================
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 12,
            head: [["S.no", "Roll No.", "Name", "Reason", "Term", "Payment ID", "Refund Date", "College", "Cashier", "Cash Counter", "Amount"]],
            body: (summaryData.refunds_given || []).map((r, index) => [
                index + 1,
                r.roll_no || "N/A",
                r.name || r.first_name || "N/A",
                r.refund_reason || "N/A",
                r.term_name || "N/A",
                r.payment_id || "N/A",
                formatDate(r.refund_date),
                r.college || "N/A",
                r.cashier_name || "N/A",
                r.cash_counter_name || "N/A",
                r.amount || 0,
            ]),
            styles: { fontSize: 9 },
            columnStyles: { 10: { halign: "right" } }
        });

        // Total Refunds
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 5,
            body: [["Total Refunds", cleanText(formatCurrencyPdf(summary.total_refunds_amount ?? 0))]],
            theme: "grid",
            styles: { fontSize: 10 },
            columnStyles: { 0: { halign: "left" }, 1: { halign: "right" } }
        });

        // =============== Final Summary ==================
        //       autoTable(doc, {
        //     startY: doc.lastAutoTable.finalY + 12,
        //     head: [["Metric", "Value"]],
        //     body: [
        //         ["Total Payments", cleanText(formatCurrencyPdf(summary.total_payments_amount ?? 0))],
        //         ["Total Refunds", cleanText(formatCurrencyPdf(summary.total_refunds_amount ?? 0))],
        //         [
        //             "Net Amount",
        //             cleanText(
        //                 formatCurrencyPdf(
        //                     summary.net_amount_in_cashcounter ??
        //                     summary.net_amount ??
        //                     (summary.total_payments_amount || 0) -
        //                     (summary.total_refunds_amount || 0)
        //                 )
        //             )
        //         ],
        //     ],
        //     styles: { fontSize: 10 },
        //     columnStyles: { 1: { halign: "right" } }
        // });
        autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 12,
            head: [["Metric", "Value"]],
            body: [
                ["Total Payments", cleanText(formatCurrencyPdf(summary.total_payments_amount ?? 0))],
                ["Total Refunds", cleanText(formatCurrencyPdf(summary.total_refunds_amount ?? 0))],
            ],
            styles: { fontSize: 10 },
            columnStyles: { 1: { halign: "right" } }
        });
        doc.save(`Total_Summary_${title}.pdf`);
    };



    return (
        <div className={`modal fade ${show ? "show d-block" : ""}`} tabIndex="-1"
            style={{ backgroundColor: show ? "rgba(0,0,0,0.5)" : "transparent" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title h4">Full Total Summary — {cashier}</h5>
                        <button className="btn-close" onClick={onClose}></button>
                    </div>

                    <div className="modal-body">
                        <h4 className="fw-bold mb-3">Summary</h4>

                        {summaryData?.summary ? (
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Net Amount</div>
                                        <div className="h4 fw-bold">
                                            {formatCurrency(
                                                summaryData.summary.net_amount_in_cashcounter ??
                                                summaryData.summary.net_amount ??
                                                (summaryData.summary.total_payments_amount ?? 0) -
                                                (summaryData.summary.total_refunds_amount ?? 0)
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Total Payments</div>
                                        <div className="h4 fw-bold text-success">
                                            {formatCurrency(summaryData.summary.total_payments_amount ?? 0)} (
                                            {summaryData.summary.total_payments_count ?? 0})
                                        </div>
                                    </div>
                                </div>

                                <div className="col-md-4">
                                    <div className="p-3 bg-light rounded-3">
                                        <div className="small text-muted">Total Refunds</div>
                                        <div className="h4 fw-bold text-danger">
                                            {formatCurrency(summaryData.summary.total_refunds_amount ?? 0)} (
                                            {summaryData.summary.total_refunds_count ?? 0})
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : "No summary available."}

                        <h4 className="fw-bold mt-4">Payments</h4>
                        <PaymentsTable paymentsData={summaryData?.all_payments || []} />

                        <h4 className="fw-bold mt-4">Refunds</h4>
                        <RefundsTable refundData={summaryData?.refunds_given || []} />
                    </div>

                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}>Close</button>
                        <button className="btn btn-success" onClick={handleExcelDownload}>Download Excel</button>
                        <button className="btn btn-danger" onClick={handlePdfDownload}>Download PDF</button>

                    </div>
                </div>
            </div>
        </div>
    );
}


/* ================================
   Payments Statistics
================================ */


function PaymentsStatistics({ cashierNames }) {
    const [selectedStat, setSelectedStat] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedCashierName, setSelectedCashierName] = useState("All");

    const [totalData, setTotalData] = useState(null);

    const [todaySummaryData, setTodaySummaryData] = useState(null);

    const [selectedDate, setSelectedDate] = useState("");
    const [datePaymentsData, setDatePaymentsData] = useState(null);
    const [isDateLoading, setIsDateLoading] = useState(false);
    const [dateError, setDateError] = useState(null);

    const [showTodayModal, setShowTodayModal] = useState(false);
    const [showDateModal, setShowDateModal] = useState(false);
    const [dateModalData, setDateModalData] = useState(null);

    const [showTotalModal, setShowTotalModal] = useState(false);
    const [user, setUser] = useState();



    const cashier_names_list = cashierNames?.map((ele) => ele.first_name) || [];
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        const authUser = JSON.parse(localStorage.getItem("authUser"));
        setUser(authUser)
    }, [backendUrl])
    // Normalize helpers (accept both summary key styles)
    const toTodayShapeFromOverallByDate = (api) => ({
        today_payments: api.all_payments ?? [],
        today_refunds: api.refunds_given ?? [],
        summary: {
            total_payments_today: api.summary?.total_payments_amount ?? 0,
            total_payments_count: api.summary?.total_payments_count ?? 0,
            total_refunds_today: api.summary?.total_refunds_amount ?? 0,
            total_refunds_count: api.summary?.total_refunds_count ?? 0,
            net_amount_in_cashcounter:
                api.summary?.net_amount ??
                (api.summary?.total_payments_amount ?? 0) - (api.summary?.total_refunds_amount ?? 0),
        },
    });

    const toTotalsFromOverall = (arr = []) => {
        const amount = arr.reduce((s, i) => s + (i.amount || 0), 0);
        return { amount, count: arr.length };
    };

    // Initial fetch (ALL)
    useEffect(() => {
        const fetchPaymentsData = async () => {
            setIsLoading(true);
            setError(null);

            try {
                const results = await Promise.allSettled([
                    api.post(`${backendUrl}course-payments-overall`, {
                        role: user?.role
                    }),
                    api.post(`${backendUrl}course-payments-overall-today`,
                        {
                            role: user?.role
                        }
                    ),
                ]);

                let fetchError = null;

                // ✅ TOTAL (ALL) – now has summary + refunds_given
                if (results[0].status === "fulfilled" && results[0].value.data?.success) {
                    const api = results[0].value.data.data; // { all_payments, refunds_given, summary }
                    // console.log("Fetched total payments data:", api);
                    setTotalData({
                        all_payments: api.all_payments || [],
                        refunds_given: api.refunds_given || [],
                        summary: api.summary || null,
                    });
                } else {
                    fetchError = "Failed to fetch total payments.";
                }

                // ✅ TODAY (ALL)
                if (results[1].status === "fulfilled" && results[1].value.data?.success) {
                    setTodaySummaryData(results[1].value.data.data);
                } else {
                    fetchError = (fetchError || "") + " Failed to fetch today's payments.";
                }

                if (fetchError) setError(fetchError);
            } catch {
                setError("Unexpected error.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchPaymentsData();
    }, [backendUrl, user]);


    // Cashier Filter
    const handleCashierChange = async (e) => {
        const newCashierName = e.target.value;
        setSelectedCashierName(newCashierName);
        setSelectedStat(null);
        setDatePaymentsData(null);
        setDateError(null);
        setIsLoading(true);
        setError(null);

        try {
            let totalReq, todayReq;

            if (newCashierName === "All") {
                totalReq = api.post(`${backendUrl}course-payments-overall`, {
                    role: user?.role
                });
                todayReq = api.post(`${backendUrl}course-payments-overall-today`, {
                    role: user?.role
                });
            } else {
                totalReq = api.post(`${backendUrl}course-payments-overall-by-cashier-total`, {
                    cashier_name: newCashierName,
                });
                todayReq = api.post(`${backendUrl}course-payments-overall-today-by-cashier-name`, {
                    cashier_name: newCashierName,
                });
            }

            const results = await Promise.allSettled([totalReq, todayReq]);
            let fetchError = null;

            // ✅ TOTAL (ALL or CASHIER) – expect { all_payments, refunds_given, summary }
            if (results[0].status === "fulfilled" && results[0].value.data?.success) {
                const api = results[0].value.data.data || {};
                setTotalData({
                    all_payments: api.all_payments || [],
                    refunds_given: api.refunds_given || [],
                    summary: api.summary || null,
                });
            } else {
                fetchError = `Failed to fetch total for ${newCashierName}`;
            }

            // ✅ TODAY (ALL or CASHIER) – keep using "today" API exclusively for today summary
            if (results[1].status === "fulfilled" && results[1].value.data?.success) {
                const api = results[1].value.data.data || {};
                setTodaySummaryData({
                    today_payments: api.today_payments || [],
                    today_refunds: api.today_refunds || [],
                    summary: api.summary || {},
                });
            } else {
                fetchError = (fetchError || "") + ` Failed to fetch today's data for ${newCashierName}`;
            }

            if (fetchError) setError(fetchError);
        } catch {
            setError("Unexpected error while filtering.");
        } finally {
            setIsLoading(false);
        }
    };



    const handlePaymentDateChange = async (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);

        if (!newDate) {
            setDatePaymentsData(null);
            setDateError(null);
            return;
        }

        setIsDateLoading(true);
        setDateError(null);
        setDatePaymentsData(null);

        try {
            let response;

            if (selectedCashierName === "All") {
                // ✅ All cashiers
                response = await api.post(`${backendUrl}course-payments-overall-by-date`, {
                    date: newDate,
                    role: user?.role

                });
            } else {
                // ✅ Specific cashier
                response = await api.post(`${backendUrl}course-payments-overall-by-cashier-by-date`, {
                    date: newDate,
                    cashier_name: selectedCashierName
                });
            }

            if (response.data?.success) {
                const api = response.data.data;

                // ✅ Normalize backend to UI expected format
                setDatePaymentsData({
                    today_payments: api.all_payments || [],
                    today_refunds: api.refunds_given || [],
                    summary: {
                        total_payments_today: api.summary.total_payments_amount,
                        total_payments_count: api.summary.total_payments_count,
                        total_refunds_today: api.summary.total_refunds_amount,
                        total_refunds_count: api.summary.total_refunds_count,
                        net_amount_in_cashcounter: api.summary.net_amount_in_cashcounter || api.summary.net_amount
                    }
                });

                setSelectedStat("byDate");
            } else {
                setDateError("No data found.");
            }

        } catch (err) {
            setDateError("Failed to fetch data.");
        } finally {
            setIsDateLoading(false);
        }
    };


    /* UI */
    if (isLoading)
        return (
            <div className="p-5 text-center">
                <div className="spinner-border text-success"></div>
            </div>
        );

    if (error) return <div className="alert alert-danger">{error}</div>;

    const dateCardAmount =
        (datePaymentsData?.today_payments || []).reduce((a, b) => a + (b.amount || 0), 0) || 0;
    const dateCardCount = datePaymentsData?.today_payments?.length || 0;

    return (
        <div className="p-0 payments-statistics">

            {/* --- STAT CARDS ROW (Now includes Filter Card) --- */}
            <div className="row g-4 mb-4">

                {/* --- NEW: Filter Card --- */}
                <div className="col-lg-3 col-md-6">
                    <div className="bg-white rounded-4 shadow-sm p-4 h-100">
                        <div>
                            <h6 className="fw-bold text-dark mb-1">Filter Payment Data</h6>
                            <p className="text-muted small mb-3">Select a cashier to view payment and refund details</p>
                        </div>
                        <div className="filter-control">
                            <label
                                htmlFor="cashierFilter"
                                className="form-label fw-semibold text-muted mb-1 small"
                            >
                                Cashier:
                            </label>
                            <br />
                            <select
                                id="cashierFilter"
                                className="form-select modern-select"
                                value={selectedCashierName}
                                onChange={handleCashierChange}
                                style={{
                                    width: "180px",        // set your preferred width
                                    maxWidth: "100%",      // prevent overflow
                                    display: "block"
                                }}
                            >
                                <option value="All">All Cashiers</option>
                                {cashier_names_list.map((c) => (
                                    <option key={c} value={c}>
                                        {c}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Today Summary */}
                <div className="col-lg-3 col-md-6">
                    <TodaySummaryCard
                        summary={todaySummaryData?.summary}
                        isSelected={selectedStat === "today"}
                        onClick={() => setSelectedStat(selectedStat === "today" ? null : "today")}
                    />
                </div>

                {/* Total Summary */}
                <div className="col-lg-3 col-md-6">
                    <TotalSummaryCard
                        summary={totalData?.summary}
                        isSelected={selectedStat === "total"}
                        onClick={() => setSelectedStat(selectedStat === "total" ? null : "total")}
                    />
                </div>

                {/* By Date */}
                <div className="col-lg-3 col-md-6">
                    <DateStatCard
                        title={
                            selectedCashierName === "All"
                                ? "Payments by Date"
                                : `Payments by Date (${selectedCashierName})`
                        }
                        amount={dateCardAmount}
                        count={dateCardCount}
                        selectedDate={selectedDate}
                        onDateChange={handlePaymentDateChange}
                        isLoading={isDateLoading}
                        error={dateError}
                        isSelected={selectedStat === "byDate"}
                        data={datePaymentsData}
                        onClick={() => {
                            if (datePaymentsData)
                                setSelectedStat(selectedStat === "byDate" ? null : "byDate");
                        }}
                    />
                </div>
            </div>

            {/* --- DETAILS SECTION --- */}
            <div className="mt-5">
                <DetailBox>
                    {selectedStat === "total" && (
                        <>
                            <h5 className="fw-bold">Payments</h5>
                            <PaymentsTable paymentsData={totalData?.all_payments || []} />

                            <h5 className="fw-bold mt-4">Refunds</h5>
                            <RefundsTable refundData={totalData?.refunds_given || []} />

                            <div className="text-end mt-3">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowTotalModal(true)}
                                >
                                    View Full Report
                                </button>
                            </div>
                        </>
                    )}

                    {selectedStat === "today" && (
                        <>
                            <h5 className="fw-bold">Today's Payments</h5>
                            <PaymentsTable paymentsData={todaySummaryData?.today_payments || []} />

                            <h5 className="fw-bold mt-4">Today's Refunds</h5>
                            <TodayRefundsTable refundData={todaySummaryData?.today_refunds || []} />

                            <div className="text-end mt-3">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowTodayModal(true)}
                                >
                                    View Full Report
                                </button>
                            </div>
                        </>
                    )}

                    {selectedStat === "byDate" && (
                        <>
                            <h5 className="fw-bold">Payments</h5>
                            <PaymentsTable paymentsData={datePaymentsData?.today_payments || []} />

                            <h5 className="fw-bold mt-4">Refunds</h5>
                            <TodayRefundsTable refundData={datePaymentsData?.today_refunds || []} />


                            <div className="text-end mt-3">
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setDateModalData(datePaymentsData);
                                        setShowDateModal(true);
                                    }}
                                >
                                    View Full Report
                                </button>
                            </div>
                        </>
                    )}

                    {!selectedStat && (
                        <div className="text-center p-5 border-dashed rounded-3">
                            <p className="text-muted">Click a card to view details.</p>
                        </div>
                    )}
                </DetailBox>
            </div>

            {/* --- MODALS --- */}
            <TodaySummaryModal
                show={showTodayModal}
                onClose={() => setShowTodayModal(false)}
                summaryData={todaySummaryData}
                college={selectedCashierName}
            />
            <DateSummaryModal
                show={showDateModal}
                onClose={() => setShowDateModal(false)}
                summaryData={dateModalData}
                date={selectedDate}
            />
            <TotalSummaryModal
                show={showTotalModal}
                onClose={() => setShowTotalModal(false)}
                summaryData={totalData}
                cashier={selectedCashierName}
            />
        </div>
    );

}

/* ================================
   App (Wrapper)
================================ */
export default function App() {

    const [activeSection, setActiveSection] = useState("payments");
    const [collegeNames, setCollegeNames] = useState();
    const [cashierNames, setCashierNames] = useState();
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const [user, setUser] = useState();

    useEffect(() => {
        let authUser = JSON.parse(localStorage.getItem('authUser'));
        setUser(authUser)
        // console.log(authUser)
    }, [])

    useEffect(() => {
        const fetchCollegeNames = async () => {
            try {
                if (backendUrl) {
                    const response = await api.get(`${backendUrl}get-cashcounter-college-names`);
                    if (response.status === 200) setCollegeNames(response.data.data);
                }
            } catch (err) {
                console.error("Failed to fetch college names:", err);
            }
        };
        fetchCollegeNames();

        const fetchCashierNames = async () => {
            try {
                if (backendUrl) {
                    const response = await api.get(`${backendUrl}get-cashiers`);
                    if (response.status === 200) {
                        setCashierNames(response.data.data);
                    }
                }
            } catch (err) {
                console.log(err);
            }
        };
        fetchCashierNames();
    }, [backendUrl]);

    // Load Excel helper
    useEffect(() => {
        const scriptId = "xlsx-script";
        if (!document.getElementById(scriptId)) {
            const script = document.createElement("script");
            script.id = scriptId;
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
            script.async = true;
            document.head.appendChild(script);
        }
    }, []);

    return (
        <div className="px-4 px-md-5 bg-light min-vh-100 font-sans-serif dashboard-container">

            {/* --- DASHBOARD HEADER --- */}
            <div className="d-flex justify-content-between align-items-center mb-2 dashboard-header">
                <h2 className="fw-bold text-dark mb-0">Finance Overview</h2>
                <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill shadow-sm">
                    {user?.role === "developer" ? "Developer Access" : "Accounts Access"}
                </span>
            </div>

            {/* --- MAIN CARD GRID --- */}
            <div className="row g-4 dashboard-card-grid">
                {/* PAYMENTS */}
                <div className="col-md-3 col-sm-3">
                    <div
                        onClick={() => setActiveSection("payments")}
                        className={`dashboard-card ${activeSection === "payments" ? "active" : ""}`}
                    >
                        <div className="icon-wrapper bg-gradient-success">
                            <PaymentIcon className="icon-lg text-white" />
                        </div>
                        <div>
                            <h5 className="fw-bold mb-1 text-dark">Payments</h5>
                            <p className="text-muted small mb-0">View and manage all payments</p>
                        </div>
                    </div>
                </div>

                {/* REFUNDS */}
                <div className="col-md-3 col-sm-3">
                    <div
                        onClick={() => setActiveSection("refunds")}
                        className={`dashboard-card ${activeSection === "refunds" ? "active" : ""}`}
                    >
                        <div className="icon-wrapper bg-gradient-danger">
                            <RefundIcon className="icon-lg text-white" />
                        </div>
                        <div>
                            <h5 className="fw-bold mb-1 text-dark">Refunds</h5>
                            <p className="text-muted small mb-0">Track and process all refunds</p>
                        </div>
                    </div>
                </div>

                {/* DISCOUNTS (Developer only) */}
                {/* {user?.role === "developer" && (
                    <div className="col-md-4 col-sm-6">
                        <div
                            onClick={() => setActiveSection("discounts")}
                            className={`dashboard-card ${activeSection === "discounts" ? "active" : ""}`}
                        >
                            <div className="icon-wrapper bg-gradient-warning">
                                <DiscountIcon className="icon-lg text-white" />
                            </div>
                            <div>
                                <h5 className="fw-bold mb-1 text-dark">Discounts</h5>
                                <p className="text-muted small mb-0">Manage discount offers and approvals</p>
                            </div>
                        </div>
                    </div>
                )} */}
            </div>

            {/* --- DETAIL SECTION --- */}
            <div className="mt-5">
                {activeSection === "payments" && <PaymentsStatistics cashierNames={cashierNames} />}
                {activeSection === "refunds" && <TotalRefundsStatistics collegeNames={collegeNames} />}
                {activeSection === "discounts" && (
                    <DetailBox title="Discounts Management">
                        <div className="text-center border-dashed p-5 rounded-3 bg-white shadow-sm">
                            <p className="text-muted fw-medium mb-0">
                                Discount creation and management will be shown here.
                            </p>
                        </div>
                    </DetailBox>
                )}
            </div>
        </div>

    );
}
